/**
 * Styled System Factory
 * Creates styled components with Chakra-style props support
 * Built on top of @emotion/styled
 */

import styled from '@emotion/styled';
import { css as emotionCss } from '@emotion/react';
import { transformStyleProps, splitProps, stylePropsKeys } from './style-props';

/**
 * Create a chakra-styled component
 * Supports style props, sx prop, and __css internal styles
 * 
 * @param {string|Component} element - HTML element or React component
 * @param {object} options - Configuration options
 * @returns {Component} - Styled component with Chakra-style props
 */
export const chakra = (element, options = {}) => {
  const { baseStyle, ...styledOptions } = options;

  const StyledComponent = styled(element, {
    // Filter out style props from being passed to DOM
    shouldForwardProp: (prop) => {
      // Don't forward Chakra style props to the DOM
      if (stylePropsKeys.includes(prop)) return false;
      // Don't forward internal props
      if (prop === 'sx' || prop === '__css' || prop === 'as') return false;
      return true;
    },
    ...styledOptions,
  })(({ theme, ...props }) => {
    const styles = [];

    // Apply base styles from options
    if (baseStyle) {
      const baseStyleResult = typeof baseStyle === 'function' 
        ? baseStyle({ theme, ...props }) 
        : baseStyle;
      styles.push(transformStyleProps(baseStyleResult, theme));
    }

    // Apply __css internal styles (from component definitions)
    if (props.__css) {
      const cssResult = typeof props.__css === 'function'
        ? props.__css({ theme, ...props })
        : props.__css;
      styles.push(transformStyleProps(cssResult, theme));
    }

    // Apply style props passed directly to the component
    const [styleProps] = splitProps(props);
    styles.push(transformStyleProps(styleProps, theme));

    // Apply sx prop (user overrides)
    if (props.sx) {
      const sxResult = typeof props.sx === 'function'
        ? props.sx({ theme, ...props })
        : props.sx;
      styles.push(transformStyleProps(sxResult, theme));
    }

    return styles;
  });

  // Add displayName for debugging
  if (typeof element === 'string') {
    StyledComponent.displayName = `chakra.${element}`;
  }

  return StyledComponent;
};

/**
 * Factory object for creating chakra-styled HTML elements
 * Usage: chakra.div, chakra.span, chakra.button, etc.
 */
const htmlElements = [
  'a', 'abbr', 'address', 'area', 'article', 'aside', 'audio',
  'b', 'base', 'bdi', 'bdo', 'big', 'blockquote', 'body', 'br', 'button',
  'canvas', 'caption', 'cite', 'code', 'col', 'colgroup',
  'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt',
  'em', 'embed',
  'fieldset', 'figcaption', 'figure', 'footer', 'form',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html',
  'i', 'iframe', 'img', 'input', 'ins',
  'kbd', 'keygen',
  'label', 'legend', 'li', 'link',
  'main', 'map', 'mark', 'menu', 'menuitem', 'meta', 'meter',
  'nav', 'noscript',
  'object', 'ol', 'optgroup', 'option', 'output',
  'p', 'param', 'picture', 'pre', 'progress',
  'q',
  'rp', 'rt', 'ruby',
  's', 'samp', 'script', 'section', 'select', 'small', 'source', 'span', 'strong', 'style', 'sub', 'summary', 'sup', 'svg',
  'table', 'tbody', 'td', 'template', 'textarea', 'tfoot', 'th', 'thead', 'time', 'title', 'tr', 'track',
  'u', 'ul',
  'var', 'video',
  'wbr',
  // SVG elements
  'circle', 'clipPath', 'defs', 'ellipse', 'foreignObject', 'g',
  'image', 'line', 'linearGradient', 'mask', 'path', 'pattern', 'polygon', 'polyline',
  'radialGradient', 'rect', 'stop', 'text', 'tspan',
];

// Create factory methods for each HTML element
htmlElements.forEach((element) => {
  chakra[element] = chakra(element);
});

/**
 * CSS helper function
 * Allows using Chakra-style props in css prop or styled blocks
 */
export const css = (styles) => (props) => {
  const { theme } = props;
  return emotionCss(transformStyleProps(styles, theme));
};

/**
 * forwardRef helper
 * Creates a component that forwards ref and supports style props
 */
export const forwardRef = (component) => {
  const { displayName, ...rest } = component;
  const WrappedComponent = styled(component)(rest);
  if (displayName) {
    WrappedComponent.displayName = displayName;
  }
  return WrappedComponent;
};

export { transformStyleProps, splitProps, stylePropsKeys };
